import * as React from 'react'

import moment from 'moment'

import {
  getNestedString,
  type OneMutationNotification,
  useOneMutationSubscription,
} from 'modules/ai/lib/useOneMutationSubscription'
import { dateToString } from 'modules/core/lib/dates'

import { Leaves } from 'modules/timeoff/store/collections/leaves'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

const leavesContext = React.createContext<Values | null>(null)

const LeavesProvider = leavesContext.Provider

interface Values {
  currentAndFutureLeaves: Leaves
  fetchData: (employeeId: string) => Promise<unknown>
  fetchCount: number
}

export const LeavesContextProvider = ({
  children,
  employeeId,
}: React.PropsWithChildren<{ employeeId?: string }>) => {
  // FIXME Hack for
  // https://linear.app/factorial/issue/FRA-292/fix-issue-with-updated-leaves-not-being-reflected-in-calendar-current
  // Maybe Context is not the best way to keep the views on leaves in sync, see
  // https://github.com/factorialco/factorial-frontend/pull/4356#discussion_r559489527
  const fetchCount = React.useRef(0)
  const currentAndFutureLeaves = React.useRef(new Leaves()).current

  const fetchData: Values['fetchData'] = (employeeId) => {
    fetchCount.current += 1

    return currentAndFutureLeaves.fetch({
      data: {
        employee_id: employeeId,
        terminated: true,
        from: dateToString(moment([moment().year(), 0, 1]), 'backend'),
        include_all_pending_approvals: true,
      },
    })
  }

  const handleCreateLeave = useUnsafeCallback(
    async (payload: OneMutationNotification) => {
      const targetEmployeeId =
        getNestedString(payload.result, [
          'timeoffMutations',
          'createTimeoffLeave',
          'leave',
          'employee',
          'id',
        ]) ?? employeeId

      if (!targetEmployeeId) {
        return
      }

      if (employeeId && targetEmployeeId !== employeeId) {
        return
      }

      await fetchData(targetEmployeeId)
    },
    [employeeId, fetchData]
  )

  useOneMutationSubscription('CreateLeave', handleCreateLeave)

  const values: Values = {
    currentAndFutureLeaves,
    fetchData,
    fetchCount: fetchCount.current,
  }

  return <LeavesProvider value={values}>{children}</LeavesProvider>
}

export const useLeaves = () => {
  const data = React.useContext(leavesContext)

  if (!data) {
    throw new Error('useLeaves must be used within a collection provider')
  }

  return data
}
